import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { ChevronLeft, Building2, Loader2, Trash2, Save, AlertTriangle, Scale } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import AssignmentsPanel from '../components/cases/AssignmentsPanel'
import DocumentsPanel from '../components/cases/DocumentsPanel'
import TimelinePanel from '../components/cases/TimelinePanel'
import CaseCalendarPanel from '../components/cases/CaseCalendarPanel'
import CaseAiPanel from '../components/cases/CaseAiPanel'
import FundsProvisionPanel from '../components/billing/FundsProvisionPanel'
import MilestonesPanel from '../components/billing/MilestonesPanel'
import CaseLitigationPanel from '../components/cases/CaseLitigationPanel'
import CaseTasksPanel from '../components/cases/CaseTasksPanel'
import CaseTimePanel from '../components/cases/CaseTimePanel'
import {
  CaseStatusLabel,
  LegalAreaLabel,
  NON_LITIGATION_AREAS,
  type CaseDetail,
  type CaseStatus,
  type LegalArea,
  type UpdateCaseInput,
} from '@toga/shared'
import IssuerSelect from '../components/billing/IssuerSelect'
import { useAuth } from '../contexts/AuthContext'

export default function CaseDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const auth = useAuth()
  const canSetIssuer =
    auth.status === 'authenticated' && (auth.user.role === 'ADMIN' || auth.user.role === 'ABOGADO')
  const [data, setData] = useState<CaseDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [editForm, setEditForm] = useState<UpdateCaseInput>({})
  const [edited, setEdited] = useState(false)

  const reload = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setError(null)
    try {
      const { case: c } = await api.get<{ case: CaseDetail }>(`/cases/${id}`)
      setData(c)
      setEditForm({
        title: c.title,
        area: c.area,
        status: c.status,
        description: c.description,
        jurisdiction: c.jurisdiction,
        courtName: c.courtName,
        courtFileNumber: c.courtFileNumber,
        opposingParty: c.opposingParty,
        issuerId: c.issuerId,
      })
      setEdited(false)
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setError('Este expediente no existe o no tienes acceso.')
      } else {
        setError(err instanceof Error ? err.message : 'Error al cargar')
      }
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    void reload()
  }, [reload])

  const upd = <K extends keyof UpdateCaseInput>(k: K, v: UpdateCaseInput[K]) => {
    setEditForm((f) => ({ ...f, [k]: v }))
    setEdited(true)
  }

  const onSave = async () => {
    if (!id) return
    setSaving(true)
    try {
      await api.patch(`/cases/${id}`, editForm)
      await reload()
    } catch (err) {
      const body =
        err instanceof ApiError ? (err.body as { error?: string } | undefined) : undefined
      alert(body?.error ?? (err instanceof Error ? err.message : 'Error'))
    } finally {
      setSaving(false)
    }
  }

  const onDelete = async () => {
    if (!data) return
    if (
      !confirm(
        `¿Eliminar el expediente ${data.code}? (Soft delete: el expediente queda archivado.)`
      )
    )
      return
    try {
      await api.delete(`/cases/${data.id}`)
      navigate('/cases')
    } catch (err) {
      const body =
        err instanceof ApiError ? (err.body as { error?: string } | undefined) : undefined
      alert(body?.error ?? (err instanceof Error ? err.message : 'Error'))
    }
  }

  if (loading) {
    return (
      <div className="t-muted flex items-center gap-2">
        <Loader2 size={18} className="animate-spin" /> Cargando expediente…
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="max-w-xl mx-auto pt-12 surface p-8 text-center">
        <AlertTriangle size={32} className="mx-auto text-amber-300 mb-3" />
        <h2 className="text-xl font-serif font-semibold t-primary mb-1">No disponible</h2>
        <p className="t-muted text-sm mb-4">{error ?? 'Expediente no encontrado.'}</p>
        <Link
          to="/cases"
          className="inline-flex items-center gap-1.5 text-sm text-toga-300 hover:underline"
        >
          <ChevronLeft size={14} /> Volver al listado
        </Link>
      </div>
    )
  }

  const disabled = !data.canWrite

  const inputCls =
    'w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/60 text-slate-100 focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60 disabled:opacity-60'

  return (
    <div className="space-y-6 animate-slideUp">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <Link
            to="/cases"
            className="text-xs t-faint hover:underline inline-flex items-center gap-1"
          >
            <ChevronLeft size={12} /> Expedientes
          </Link>
          <div className="flex items-baseline gap-3 mt-1">
            <span className="font-mono text-sm text-toga-300">{data.code}</span>
            <h2 className="text-2xl font-serif font-semibold t-primary flex items-center gap-2">
              <Scale size={20} className="text-toga-300" />
              {data.title}
            </h2>
          </div>
          <div className="t-muted text-sm mt-1 flex items-center gap-2">
            <Building2 size={14} />
            <Link to={`/clients/${data.clientId}`} className="hover:underline">
              {data.clientDisplayName}
            </Link>
            <span className="t-faint">·</span>
            <span>{LegalAreaLabel[data.area]}</span>
            <span className="t-faint">·</span>
            <span>{CaseStatusLabel[data.status]}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {edited && data.canWrite && (
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga disabled:opacity-50 flex items-center gap-1.5"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Guardar
            </button>
          )}
          {data.canManage && (
            <button
              type="button"
              onClick={onDelete}
              className="px-3 py-2 rounded-lg text-sm bg-rose-500/10 text-rose-300 hover:bg-rose-500/15 flex items-center gap-1.5"
              title="Soft delete"
            >
              <Trash2 size={14} /> Eliminar
            </button>
          )}
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">
        <div className="space-y-4">
          {/* Datos editables */}
          <div className="surface p-5 space-y-3">
            <h3 className="t-primary font-semibold mb-1">Datos del expediente</h3>
            <div>
              <label className="block text-xs t-muted mb-1">Título</label>
              <input
                disabled={disabled}
                value={editForm.title ?? ''}
                onChange={(e) => upd('title', e.target.value)}
                className={inputCls}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs t-muted mb-1">Estado</label>
                <select
                  disabled={disabled}
                  value={editForm.status ?? data.status}
                  onChange={(e) => upd('status', e.target.value as CaseStatus)}
                  className={inputCls}
                >
                  {Object.entries(CaseStatusLabel).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs t-muted mb-1">Área</label>
                <select
                  disabled={disabled}
                  value={editForm.area ?? data.area}
                  onChange={(e) => upd('area', e.target.value as LegalArea)}
                  className={inputCls}
                >
                  {Object.entries(LegalAreaLabel).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
              {!NON_LITIGATION_AREAS.includes(editForm.area ?? data.area) && (
                <>
                  <div>
                    <label className="block text-xs t-muted mb-1">Juzgado</label>
                    <input
                      disabled={disabled}
                      value={editForm.courtName ?? ''}
                      onChange={(e) => upd('courtName', e.target.value)}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="block text-xs t-muted mb-1">Nº procedimiento</label>
                    <input
                      disabled={disabled}
                      value={editForm.courtFileNumber ?? ''}
                      onChange={(e) => upd('courtFileNumber', e.target.value)}
                      className={`${inputCls} font-mono`}
                    />
                  </div>
                </>
              )}
              <div className="col-span-2">
                <label className="block text-xs t-muted mb-1">Facturar con (emisor)</label>
                <IssuerSelect
                  disabled={disabled || !canSetIssuer}
                  value={editForm.issuerId}
                  onChange={(id) => upd('issuerId', id)}
                  emptyLabel="El del abogado responsable, el del cliente o el del bufete"
                  className={inputCls}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs t-muted mb-1">Parte contraria</label>
                <input
                  disabled={disabled}
                  value={editForm.opposingParty ?? ''}
                  onChange={(e) => upd('opposingParty', e.target.value)}
                  className={inputCls}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs t-muted mb-1">Descripción</label>
              <textarea
                disabled={disabled}
                rows={4}
                value={editForm.description ?? ''}
                onChange={(e) => upd('description', e.target.value)}
                className={inputCls}
              />
            </div>
            {!data.canWrite && (
              <p className="text-xs t-faint">
                Solo el responsable o un administrador puede editar los datos.
              </p>
            )}
          </div>

          <CaseCalendarPanel caseId={data.id} canWrite={data.canWrite} />
          {!NON_LITIGATION_AREAS.includes(data.area) && (
            <CaseLitigationPanel
              caseId={data.id}
              caseCode={data.code}
              jurisdiction={data.jurisdiction}
              currentAgentId={data.currentAgentId}
              currentAgentName={data.currentAgentName}
              canWrite={data.canWrite}
              onAgentChanged={reload}
            />
          )}
          <CaseTasksPanel caseId={data.id} canWrite={data.canWrite} />
          <CaseTimePanel caseId={data.id} canWrite={data.canWrite} />
          <MilestonesPanel clientId={data.clientId} caseId={data.id} canWrite={data.canWrite} />
          <DocumentsPanel caseId={data.id} canWrite={data.canWrite} onChanged={reload} />
          <TimelinePanel caseId={data.id} canWrite={data.canWrite} />
        </div>

        <aside className="space-y-4">
          <CaseAiPanel caseId={data.id} caseCode={data.code} />
          <FundsProvisionPanel clientId={data.clientId} caseId={data.id} />
          <AssignmentsPanel
            caseId={data.id}
            canManage={data.canManage}
            assignments={data.assignments}
            onChanged={reload}
          />
          <div className="surface p-5 text-sm space-y-1.5">
            <h3 className="t-primary font-semibold mb-2">Detalles</h3>
            <Row label="Abierta" value={new Date(data.openedAt).toLocaleDateString('es-ES')} />
            <Row
              label="Cerrada"
              value={data.closedAt ? new Date(data.closedAt).toLocaleDateString('es-ES') : '—'}
            />
            <Row label="Creada por" value={data.createdByName ?? '—'} />
            <Row label="Documentos" value={String(data.documentsCount)} />
            <Row label="Equipo" value={String(data.assignedCount)} />
          </div>
        </aside>
      </div>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-xs">
      <span className="t-muted">{label}</span>
      <span className="t-primary font-medium">{value}</span>
    </div>
  )
}
